import React from 'react';

// Actions
import { ReduxState } from '@/redux/modules/types';
import { updatePreferences } from '@/redux/modules/auth';
import { UserPreferencesComparisonSettingOption, USER_PREFERENCES_COMPARISON_SETTINGS } from '@/redux/modules/user-preferences';

// Components
import { InfoTooltip } from '@/views/components/tooltips';
import { SelectInput, SelectOption } from '@/views/components/inputs';
import { Typography } from '@capintel/component-lib/typography';
import { withErrorBoundary } from '@/views/components/errors';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Legacy component
import LegacyComparisonSettingsOptions from './LegacyComparisonSettingsOptions';

// Libraries
import { connect } from 'react-redux';

// Types
import User from '@/types/user';
import UserPreferences from '@/types/user-preferences';
import UserPreferencesComparisonSettings from '@/types/user-preferences-comparison-settings';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

// Utils
import notification from '@/utils/notification';
import { filteredPerformanceAndRiskDataOptions } from '@/utils/preferences';
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { getInitialUserPreferences } from '@/utils/users';
import { TIME_PERIODS } from '@/utils/constants';
import { userHasPermission, PERMISSIONS } from '@/utils/permissions';

enum SETTINGS_KEYS {
  INITIAL_BENCHMARK = 'initialBenchmark',
  PERFORMANCE_AND_RISK_DATA = 'performanceAndRiskData',
  HYPOTHETICAL_RETURN_CHART_DETAIL = 'hypotheticalReturnChartDetail',
  HYPOTHETICAL_RETURN_CHART_HORIZON = 'hypotheticalReturnChartHorizon',
  BASE_CURRENCY = 'baseCurrency',
}

type SETTINGS_OPTION = {
  label: string;
  options: JSX.Element[];
  selectedOption?: boolean | string;
  settingKey: SETTINGS_KEYS;
  show: boolean;
  tooltipMessage: string;
};

type DispatchProps = {
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
};

type StateProps = {
  comparisonSettings: UserPreferencesComparisonSettings;
};

type LegacyProps = {
  user: User;
  onComparisonSettingsChange: (option: { [key: string]: string }) => void;
};

type Props = LegacyProps & StateProps & DispatchProps;

const ComparisonSettingsOptionsV2 = ({ comparisonSettings, updatePreferences, user }: Props) => {
  const { baseCurrency, hypotheticalReturnChartDetail, hypotheticalReturnChartHorizon, initialBenchmark, performanceAndRiskData } = comparisonSettings;
  const i18nBasePath = 'Settings.Preferences.ComparisonSettings.Fields';

  const getSettings = () =>
    [
      {
        label: `${i18nBasePath}.InitialBenchmark.Label`,
        options: renderOptions(USER_PREFERENCES_COMPARISON_SETTINGS.INITIAL_BENCHMARK, initialBenchmark),
        selectedOption: initialBenchmark,
        settingKey: SETTINGS_KEYS.INITIAL_BENCHMARK,
        show: userHasPermission(user, PERMISSIONS.BENCHMARKS) && userHasPermission(user, PERMISSIONS.COMPARISON_AUTO_BENCHMARK),
        tooltipMessage: `${i18nBasePath}.InitialBenchmark.Disclaimer`,
      },
      {
        label: `${i18nBasePath}.PerformanceAndRiskData.Label`,
        options: renderOptions(filteredPerformanceAndRiskDataOptions(user, true), performanceAndRiskData),
        selectedOption: performanceAndRiskData,
        settingKey: SETTINGS_KEYS.PERFORMANCE_AND_RISK_DATA,
        show: userHasPermission(user, PERMISSIONS.MONTH_END_DATA),
        tooltipMessage: `${i18nBasePath}.PerformanceAndRiskData.Disclaimer`,
      },
      {
        label: `${i18nBasePath}.HypotheticalReturnChartDetail.Label`,
        options: renderOptions(USER_PREFERENCES_COMPARISON_SETTINGS.HYPOTHETICAL_RETURN_CHART_DETAIL, hypotheticalReturnChartDetail),
        selectedOption: hypotheticalReturnChartDetail,
        settingKey: SETTINGS_KEYS.HYPOTHETICAL_RETURN_CHART_DETAIL,
        show: true,
        tooltipMessage: `${i18nBasePath}.HypotheticalReturnChartDetail.Disclaimer`,
      },
      {
        label: `${i18nBasePath}.HypotheticalReturnChartHorizon.Label`,
        options: renderOptions(
          USER_PREFERENCES_COMPARISON_SETTINGS.HYPOTHETICAL_RETURN_CHART_HORIZON,
          hypotheticalReturnChartHorizon,
          SETTINGS_KEYS.HYPOTHETICAL_RETURN_CHART_HORIZON,
        ),
        selectedOption: hypotheticalReturnChartHorizon,
        settingKey: SETTINGS_KEYS.HYPOTHETICAL_RETURN_CHART_HORIZON,
        show: true,
        tooltipMessage: `${i18nBasePath}.HypotheticalReturnChartHorizon.Disclaimer`,
      },
      {
        label: `${i18nBasePath}.BaseCurrency.Label`,
        options: renderOptions(USER_PREFERENCES_COMPARISON_SETTINGS.BASE_CURRENCY, baseCurrency, SETTINGS_KEYS.BASE_CURRENCY),
        selectedOption: baseCurrency,
        settingKey: SETTINGS_KEYS.BASE_CURRENCY,
        show: userHasPermission(user, PERMISSIONS.CURRENCY_CONVERSIONS),
        tooltipMessage: `${i18nBasePath}.BaseCurrency.Disclaimer`,
      },
    ].filter(setting => setting.show);

  const onComparisonSettingsChange = async (option: { [key: string]: string }) => {
    await updatePreferences({ comparisonSettings: { ...option } });
    // Use setTimeout to make sure settings have been changed before sending the notification.
    setTimeout(() => notification.success(formatMessage('Settings.Preferences.SettingChanged')), 0);
  };

  const renderOptions = (options: UserPreferencesComparisonSettingOption[], selected?: boolean | string, type?: SETTINGS_KEYS) => {
    switch (type) {
      case SETTINGS_KEYS.BASE_CURRENCY:
        return options.map(option => (
          <SelectOption key={option.value} value={option.value === 'Yes'}>
            {formatMessage(option.i18nKey)}
          </SelectOption>
        ));
      case SETTINGS_KEYS.HYPOTHETICAL_RETURN_CHART_HORIZON:
        return options.map(option => (
          <SelectOption key={option.value} value={option.value}>
            {userHasPermission(user, PERMISSIONS.COMMON_INCEPTION) && option.value === TIME_PERIODS.CI
              ? formatMessage(`${i18nBasePath}.HypotheticalReturnChartHorizon.Options.CI`)
              : formatMessage(option.i18nKey)}
          </SelectOption>
        ));
      default:
        return options.map(option => (
          <SelectOption key={option.value} value={option.value}>
            {formatMessage(option.i18nKey)}
          </SelectOption>
        ));
    }
  };

  const renderSelect = ({ label, options, selectedOption, settingKey, tooltipMessage }: SETTINGS_OPTION) => (
    <SelectInput
      className="select-input-v2"
      key={label}
      label={formatMessage(label)}
      onValueChange={value => onComparisonSettingsChange({ [settingKey]: value })}
      selected={selectedOption}
      tooltip={
        <InfoTooltip
          className="custom-tooltip-v2"
          data-private
          data-dd-privacy="mask"
          message={formatMessage(tooltipMessage)}
          place="bottom"
          title={formatMessage(label)}
        />
      }
    >
      {options}
    </SelectInput>
  );

  return (
    <div className="preferences preferences-v2" data-testid="preferences">
      <div className="preference">
        <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerSm">
          <FormattedMessage id="Settings.Preferences.ComparisonSettings.Title" />
        </Typography>
        <p className="description-text">
          <FormattedMessage id="Settings.Preferences.ComparisonSettings.Description" />
        </p>
      </div>
      <div className="options">{getSettings().map(setting => renderSelect(setting))}</div>
    </div>
  );
};

const ComparisonSettingsOptions: React.FC<React.ComponentProps<typeof ComparisonSettingsOptionsV2>> = props => {
  const { isLightVersion } = useThemeVersion();

  if (isLightVersion) {
    return <ComparisonSettingsOptionsV2 {...props} />;
  }

  return <LegacyComparisonSettingsOptions {...props} />;
};

const mapStateToProps = (state: ReduxState): StateProps => {
  let comparisonSettings = state.auth.user?.preferences?.comparisonSettings;

  if (!comparisonSettings) {
    comparisonSettings = {
      ...getInitialUserPreferences(state.auth.user),
      ...state.auth.user?.preferences?.comparisonSettings,
    };
  }

  return {
    comparisonSettings,
  };
};

export default connect(mapStateToProps, {
  updatePreferences,
})(withErrorBoundary(ComparisonSettingsOptions));
